
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export default function CTA() {
  const { t } = useTranslation('common');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const textarea = form.querySelector('textarea');
    
    if (textarea && textarea.value.length > 500) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4jvsohgb9i7lkq8r6p0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      if (response.ok) {
        setShowSuccess(true);
        form.reset();
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    } catch (error) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-teal-600 to-teal-700">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-teal-50 max-w-3xl mx-auto mb-8">
            {t('cta.subtitle')}
          </p>
        </div>
        
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl p-8 lg:p-12">
          {showSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm font-medium">{t('cta.form.alerts.success')}</p>
            </div>
          )}
          
          {showError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm font-medium">{t('cta.form.alerts.error')}</p>
            </div>
          )}
          
          <form className="space-y-6" id="contact-form" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-semibold text-gray-900 mb-2">
                  {t('cta.form.firstName')} {t('cta.form.required')}
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder={t('cta.form.placeholders.firstName')}
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-semibold text-gray-900 mb-2">
                  {t('cta.form.lastName')} {t('cta.form.required')}
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder={t('cta.form.placeholders.lastName')}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                {t('cta.form.email')} {t('cta.form.required')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder={t('cta.form.placeholders.email')}
              />
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                {t('cta.form.phone')} {t('cta.form.required')}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder={t('cta.form.placeholders.phone')}
              />
            </div>
            
            <div>
              <label htmlFor="country" className="block text-sm font-semibold text-gray-900 mb-2">
                {t('cta.form.country')} {t('cta.form.required')}
              </label>
              <input
                type="text"
                id="country"
                name="country"
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder={t('cta.form.placeholders.country')}
              />
            </div>
            
            <div>
              <label htmlFor="studyLevel" className="block text-sm font-semibold text-gray-900 mb-2">
                {t('cta.form.studyLevel')} {t('cta.form.required')}
              </label>
              <select
                id="studyLevel"
                name="studyLevel"
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm pr-8 cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">{t('cta.form.selectLevel')}</option>
                <option value="bachelor">{t('cta.form.bachelor')}</option>
                <option value="master">{t('cta.form.master')}</option>
                <option value="phd">{t('cta.form.phd')}</option>
                <option value="other">{t('cta.form.other')}</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                {t('cta.form.message')} {t('cta.form.required')}
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                required
                maxLength={500}
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder={t('cta.form.placeholders.message')}
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{t('cta.form.maxChars')}</p>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? '...' : t('cta.form.submit')}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
